import { useMutation, useQueryClient } from '@tanstack/react-query'
import { approveRun, refuseRun, ruleRun } from './mutations-api.js'

/**
 * A successful approve/refuse/rule changes a run's verdict, which can
 * affect the Runs list, that one run's own detail, and every Home
 * aggregate at once — invalidate all four families rather than trying to
 * patch each cache entry by hand.
 */
function useInvalidateAfterMutation() {
  const queryClient = useQueryClient()
  return (runId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['runs'] })
    void queryClient.invalidateQueries({ queryKey: ['run', runId] })
    void queryClient.invalidateQueries({ queryKey: ['home-aggregates'] })
    void queryClient.invalidateQueries({ queryKey: ['attention-queue'] })
  }
}

export function useApproveRun() {
  const invalidate = useInvalidateAfterMutation()
  return useMutation({
    mutationFn: ({ runId, fingerprint }: { runId: string; fingerprint: string }) =>
      approveRun(runId, fingerprint),
    onSuccess: (result, { runId }) => {
      if (!('error' in result)) invalidate(runId)
    },
  })
}

export function useRefuseRun() {
  const invalidate = useInvalidateAfterMutation()
  return useMutation({
    mutationFn: ({
      runId,
      fingerprint,
      reason,
    }: {
      runId: string
      fingerprint: string
      reason: string
    }) => refuseRun(runId, fingerprint, reason),
    onSuccess: (result, { runId }) => {
      if (!('error' in result)) invalidate(runId)
    },
  })
}

export function useRuleRun() {
  const invalidate = useInvalidateAfterMutation()
  return useMutation({
    mutationFn: ({
      runId,
      fingerprint,
      ruling,
    }: {
      runId: string
      fingerprint: string
      ruling: string
    }) => ruleRun(runId, fingerprint, ruling),
    onSuccess: (result, { runId }) => {
      if (!('error' in result)) invalidate(runId)
    },
  })
}
